import { usePurchaseFlowConfig, usePurchaseFlowProducts } from 'components/pages/PurchaseFlow/_main/contexts'
import { useCallback, useMemo, useState } from 'react'
import { AnalyticsEvent, logAnalyticsEvent } from 'utils/analytics'
import { AreaCalculationTypes, FloorPlanCertificationPropertyTypes } from '.'

import { ProductKind } from 'constants/product'
import constate from 'constate'
import { CatalogueProduct } from 'dataQueries/purchase.query'
import { ImmutableList } from 'models/helpers'

/**
 * Information for each floor of FP certification
 */
export type FloorPlanCertificationFloorItem = {
  /** The name of the floor. */
  name: string
}

/**
 * Information for FP certification
 */
export type FloorPlanCertificationInformation = {
  /** The name of land register sheet item. */
  propertyType: FloorPlanCertificationPropertyTypes
  /** Number of floors for certification. */
  numberOfFloors: number
  /** The list of floors in the item and its info. */
  floors: ImmutableList<FloorPlanCertificationFloorItem>
  /** The area calculation type for the certification. */
  areaCalculation: AreaCalculationTypes
  /** If architect stamp was requested */
  hasArchitectStamp: boolean
  /** Comment for certification */
  comment: string
}

const defaultFloorPlanCertificationInformation: FloorPlanCertificationInformation = {
  propertyType: FloorPlanCertificationPropertyTypes.APARTMENT,
  numberOfFloors: 1,
  floors: ImmutableList([
    { name: '' },
    { name: '' }
  ]),
  areaCalculation: AreaCalculationTypes.CALCULATE_WITH_25,
  hasArchitectStamp: false,
  comment: ''
}

export const [FloorPlanCertificationContextProvider, useFloorPlanCertification] = constate(() => {
  const { extraProducts } = usePurchaseFlowConfig()
  const { selectProduct, unselectProduct } = usePurchaseFlowProducts()

  const [floorPlanCertificationInformation, setFloorPlanCertificationInformation] = useState<FloorPlanCertificationInformation>(defaultFloorPlanCertificationInformation)

  const architectStampProduct = useMemo<CatalogueProduct | undefined>(() => {
    return extraProducts[ProductKind.EXTRA_ARCHITECT_STAMP]?.[0]
  }, [extraProducts])

  const extraCertifiedFloorProduct = useMemo<CatalogueProduct | undefined>(() => {
    return extraProducts[ProductKind.EXTRA_CERTIFIED_FLOOR]?.[0]
  }, [extraProducts])

  /**
   * Adds a new floor to the floor plan certification information.
   *
   * This function creates a new floor object with the given id and an empty name.
   * It then updates the floorPlanCertificationInformation state by adding the new floor or floors 
   * to the existing list of floors based on difference coming from input by props.
   * If there is an extraCertifiedFloorProduct and the updated list of floors has more than one floor, 
   * it selects correct # of the extraCertifiedFloorProduct for the newly added floor/s.
   */
  const addFloors = (addedFloors: number) => {

    const newFloor = {
      name: '',
    }

    setFloorPlanCertificationInformation(prevState => {
      let updatedFloors = prevState.floors

      for (let i = 0; i < addedFloors; i++) {
        updatedFloors = updatedFloors.push(newFloor)
      }

      const updatedFloorsWithoutFirstDefault = updatedFloors.skip(1)

      if (updatedFloorsWithoutFirstDefault.size > 1 && extraCertifiedFloorProduct) {
        selectProduct(extraCertifiedFloorProduct, updatedFloorsWithoutFirstDefault.size - 1)
      }

      return {
        ...prevState,
        floors: updatedFloors,
        numberOfFloors: updatedFloorsWithoutFirstDefault.size
      }
    })
  }

  /**
   * Removes the last floor/s from the FP Certification information based on passed number of floors removed by user.
   * If the number of floors is less than or equal to 1 (1 product is included in original price and 0 default floor is not counted), it unselects the extra certified floor product.
   * Otherwise, it updates the selection of the extra certified floor product based on current extra floors number.
   */
  const removeFloors = (removedFloors: number) => {

    setFloorPlanCertificationInformation(prevState => {
      let updatedFloors = prevState.floors

      for (let i = 0; i < removedFloors; i++) {
        updatedFloors = updatedFloors.pop()
      }

      const updatedFloorsWithoutFirstDefault = updatedFloors.skip(1)
      if (extraCertifiedFloorProduct && updatedFloorsWithoutFirstDefault.size <= 1) {
        unselectProduct(extraCertifiedFloorProduct.id)

      } else if (updatedFloorsWithoutFirstDefault.size > 1 && extraCertifiedFloorProduct) {
        selectProduct(extraCertifiedFloorProduct, updatedFloorsWithoutFirstDefault.size - 1)
      }

      return {
        ...prevState,
        floors: updatedFloors,
        numberOfFloors: updatedFloorsWithoutFirstDefault.size
      }
    })
  }

  const getFloorPlanCertificationRequestData = useCallback(() => {
    const { propertyType, numberOfFloors, floors, areaCalculation, comment } = floorPlanCertificationInformation

    return {
      propertyType,
      numberOfFloors,
      floors: ImmutableList(floors.shift().map((it, index) => {
        return {
          name: it.name,
          floorNumber: index + 1,
        }
      })), // Remove the first floor as it is used only for FE purposes
      regulatedLivingAreaCalculation: areaCalculation,
      comment
    }
  }, [floorPlanCertificationInformation])

  const logFloorPlanCertificationRequirements = useCallback(() => {
    const { propertyType, numberOfFloors, floors, areaCalculation, comment } = floorPlanCertificationInformation

    logAnalyticsEvent(AnalyticsEvent.LIVING_AREA_CALCULATION_REQUIREMENT_SET_UP, {
      PropertyType: propertyType,
      NumberFloors: numberOfFloors,
      FloorsData: floors,
      AreaCalculation: areaCalculation,
      Comments: comment,
    })
  }, [floorPlanCertificationInformation])

  return {
    architectStampProduct,
    removeFloors,
    addFloors,
    floorPlanCertificationInformation,
    setFloorPlanCertificationInformation,
    getFloorPlanCertificationRequestData,
    logFloorPlanCertificationRequirements,
  }
})
